import { useEffect, useState } from "react";
import { Key, Plus, RefreshCw, Ban, Trash2, Copy, Loader2, AlertTriangle, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useLang } from "@/i18n";
import { listMyApiKeys, createMyApiKey, rotateMyApiKey, revokeMyApiKey, deleteMyApiKey } from "@/lib/api";
import { formatApiError } from "@/contexts/AuthContext";

const PLAN_LABEL = {
  free: { tr: "Free", en: "Free" },
  pro: { tr: "Pro", en: "Pro" },
  enterprise: { tr: "Enterprise", en: "Enterprise" },
};

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function ApiKeysPanel({ userPlan = "free" }) {
  const { lang } = useLang();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyPlan, setNewKeyPlan] = useState(userPlan || "free");
  const [revealedKey, setRevealedKey] = useState(null); // {api_key, name, plan, rotated_from?}
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    listMyApiKeys()
      .then(setKeys)
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "load failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setNewKeyPlan(userPlan || "free"); }, [userPlan]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const res = await createMyApiKey({ name: newKeyName.trim(), plan: newKeyPlan });
      setRevealedKey(res);
      setCreateOpen(false);
      setNewKeyName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (lang === "tr" ? "Anahtar oluşturulamadı" : "Failed to create key"));
    } finally {
      setCreating(false);
    }
  };

  const handleRotate = async (id) => {
    if (!window.confirm(lang === "tr"
      ? "Bu anahtardan yeni bir hash-güvenli anahtar türetilecek. Eski anahtar sen manuel olarak iptal edene kadar çalışmaya devam edecek. Devam edilsin mi?"
      : "This will generate a new hash-secured key. The old one keeps working until you manually revoke it. Continue?")) return;
    setBusyId(id);
    try {
      const res = await rotateMyApiKey(id);
      setRevealedKey({ ...res, rotated: true });
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || (lang === "tr" ? "Yenileme başarısız" : "Rotate failed"));
    } finally {
      setBusyId(null);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm(lang === "tr" ? "Bu anahtar iptal edilecek ve API çağrıları 403 dönecek. Emin misin?" : "This key will be revoked and API calls will return 403. Are you sure?")) return;
    setBusyId(id);
    try {
      await revokeMyApiKey(id);
      toast.success(lang === "tr" ? "Anahtar iptal edildi" : "Key revoked");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "revoke failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(lang === "tr" ? "Anahtar kalıcı olarak silinecek. Bu işlem geri alınamaz." : "Key will be permanently deleted. This cannot be undone.")) return;
    setBusyId(id);
    try {
      await deleteMyApiKey(id);
      toast.success(lang === "tr" ? "Silindi" : "Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-neutral-950 border border-neutral-800 rounded-sm" data-testid="api-keys-panel">
      <div className="px-6 py-4 border-b border-neutral-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key size={18} className="text-red-500" />
          <h2 className="headline text-xl text-white">{lang === "tr" ? "API Anahtarları" : "API Keys"}</h2>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm transition-colors"
          data-testid="create-api-key-btn"
        >
          <Plus size={14} /> {lang === "tr" ? "Yeni Anahtar" : "New Key"}
        </button>
      </div>

      {keys.some((k) => k.legacy_storage && !k.revoked_at) && (
        <div className="px-6 py-3 bg-yellow-950/30 border-b border-yellow-900 text-yellow-200 text-xs flex items-start gap-2" data-testid="legacy-banner">
          <ShieldAlert size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            {lang === "tr"
              ? "Eski (plaintext) depolamayla oluşturulmuş anahtarların var. Güvenlik için her birinin yanındaki Yenile'ye tıklayıp hash-korumalı bir anahtar üret, entegrasyonunu güncelle, ardından eski anahtarı iptal et."
              : "You have legacy plaintext-stored keys. For security, click Rotate next to each one to mint a hash-secured key, migrate your integration, then revoke the old one."}
          </span>
        </div>
      )}

      {loading ? (
        <div className="py-12 flex items-center justify-center"><Loader2 className="animate-spin text-red-500" /></div>
      ) : keys.length === 0 ? (
        <div className="py-12 text-center text-neutral-500" data-testid="no-api-keys">
          {lang === "tr" ? "Henüz anahtarın yok. 'Yeni Anahtar' ile oluştur." : "No keys yet. Create one with 'New Key'."}
        </div>
      ) : (
        <div className="divide-y divide-neutral-800">
          {keys.map((k) => {
            const revoked = !!k.revoked_at;
            const legacy = !!k.legacy_storage;
            return (
              <div key={k.id} className="px-6 py-4" data-testid={`api-key-row-${k.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm text-white font-medium truncate">{k.name || "(unnamed)"}</p>
                      <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-neutral-900 border border-neutral-800 text-neutral-400">
                        {PLAN_LABEL[k.plan]?.[lang] || k.plan}
                      </span>
                      {revoked && (
                        <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-red-950/60 border border-red-900 text-red-400" data-testid={`revoked-${k.id}`}>
                          {lang === "tr" ? "İptal edildi" : "Revoked"}
                        </span>
                      )}
                      {legacy && !revoked && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm bg-yellow-950/60 border border-yellow-900 text-yellow-400" data-testid={`legacy-${k.id}`}>
                          <ShieldAlert size={11} /> Legacy
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-neutral-500 font-mono break-all" data-testid={`masked-${k.id}`}>{k.masked}</p>
                    <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-neutral-600">
                      {lang === "tr" ? "Oluşturuldu" : "Created"} {new Date(k.created_at).toLocaleString(lang === "tr" ? "tr-TR" : "en-US")}
                      {k.last_used_at && (<> · {lang === "tr" ? "son kullanım" : "last used"} {new Date(k.last_used_at).toLocaleString(lang === "tr" ? "tr-TR" : "en-US")}</>)}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {!revoked && legacy && (
                      <button
                        onClick={() => handleRotate(k.id)}
                        disabled={busyId === k.id}
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm border border-yellow-800 text-yellow-400 hover:bg-yellow-950/50 transition-colors disabled:opacity-50"
                        data-testid={`rotate-${k.id}`}
                      >
                        <RefreshCw size={12} /> {lang === "tr" ? "Yenile" : "Rotate"}
                      </button>
                    )}
                    {!revoked && (
                      <button
                        onClick={() => handleRevoke(k.id)}
                        disabled={busyId === k.id}
                        className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm border border-neutral-800 text-neutral-300 hover:border-red-500 hover:text-red-400 transition-colors disabled:opacity-50"
                        data-testid={`revoke-${k.id}`}
                      >
                        <Ban size={12} /> {lang === "tr" ? "İptal" : "Revoke"}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(k.id)}
                      disabled={busyId === k.id}
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-sm border border-neutral-800 text-neutral-400 hover:border-red-600 hover:text-red-500 transition-colors disabled:opacity-50"
                      data-testid={`delete-${k.id}`}
                    >
                      <Trash2 size={12} /> {lang === "tr" ? "Sil" : "Delete"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle className="headline text-2xl">{lang === "tr" ? "Yeni API Anahtarı" : "New API Key"}</DialogTitle>
            <DialogDescription>
              {lang === "tr"
                ? "Anahtar SADECE oluşturulduğu anda tam olarak gösterilir. Sonrasında yalnızca maskeli görünür."
                : "The key is shown in full ONLY at creation time. Afterwards you'll only see a masked version."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">{lang === "tr" ? "Anahtar Adı" : "Key name"}</label>
              <input
                required
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder={lang === "tr" ? "örn. Production" : "e.g. Production"}
                className="mt-1 w-full bg-neutral-900 border border-neutral-800 text-white rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                data-testid="new-key-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">{lang === "tr" ? "Plan" : "Plan"}</label>
              <Select value={newKeyPlan} onValueChange={setNewKeyPlan}>
                <SelectTrigger className="mt-1" data-testid="new-key-plan-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free" data-testid="new-key-plan-free">Free</SelectItem>
                  <SelectItem value="pro" disabled={userPlan === "free"} data-testid="new-key-plan-pro">
                    Pro {userPlan === "free" ? (lang === "tr" ? "(abonelik gerekli)" : "(subscription required)") : ""}
                  </SelectItem>
                  <SelectItem value="enterprise" disabled={userPlan !== "enterprise"} data-testid="new-key-plan-enterprise">
                    Enterprise {userPlan !== "enterprise" ? (lang === "tr" ? "(abonelik gerekli)" : "(subscription required)") : ""}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={creating}
                className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-sm uppercase tracking-wider font-bold text-sm transition-colors disabled:opacity-60"
                data-testid="submit-create-key"
              >
                {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                {lang === "tr" ? "Oluştur" : "Create"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* One-time revealed key dialog */}
      <Dialog open={!!revealedKey} onOpenChange={(v) => !v && setRevealedKey(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="revealed-key-dialog">
          <DialogHeader>
            <DialogTitle className="headline text-2xl flex items-center gap-2">
              <CheckCircle2 className="text-green-500" size={22} />
              {lang === "tr" ? "Anahtar Oluşturuldu" : "Key Created"}
            </DialogTitle>
            <DialogDescription>
              {revealedKey?.rotated
                ? (lang === "tr" ? "Yeni hash-korumalı anahtar oluşturuldu. Eski anahtar hâlâ aktif — entegrasyonunu bu yeni anahtara geçir ve ardından eski anahtarı iptal et." : "New hash-secured key created. Old key is still active — migrate your integration to this new key, then revoke the old one.")
                : (lang === "tr" ? "Anahtar aşağıda. Bu ekranı kapattığında bir daha görüntülenemez." : "Your key is below. Once you close this screen it cannot be shown again.")}
            </DialogDescription>
          </DialogHeader>

          <div className="bg-yellow-950/40 border border-yellow-900 text-yellow-200 rounded-sm p-3 text-xs flex items-start gap-2">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{lang === "tr" ? "Anahtarı hemen şifre yöneticisine kaydet. Bir daha gösterilmeyecek." : "Copy this key to your password manager now — it will not be shown again."}</span>
          </div>

          <div className="bg-black text-green-400 font-mono text-xs p-4 rounded-sm break-all border border-neutral-800" data-testid="revealed-key-value">
            {revealedKey?.api_key}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => {
                if (copyToClipboard(revealedKey?.api_key || "")) {
                  toast.success(lang === "tr" ? "Kopyalandı" : "Copied");
                }
              }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white py-2.5 rounded-sm uppercase tracking-wider font-bold text-xs transition-colors border border-neutral-800"
              data-testid="copy-revealed-key"
            >
              <Copy size={14} /> {lang === "tr" ? "Kopyala" : "Copy"}
            </button>
            <button
              onClick={() => setRevealedKey(null)}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-sm uppercase tracking-wider font-bold text-xs transition-colors"
              data-testid="close-revealed-key"
            >
              {lang === "tr" ? "Kaydettim, kapat" : "I saved it, close"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
